import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Search, ArrowUpRight, Loader2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { listPublishedPosts, listCategories, mediaUrl } from "../lib/api";
import { useContent } from "../context/ContentContext";
import SEO from "../seo/SEO";
import { STATIC_PUBLIC_ROUTES, getPageSeo, composeTitle, getDefaultDescription } from "../seo/routeConfig";

const blogSeoDefaults = STATIC_PUBLIC_ROUTES.find((r) => r.path === "/blog").seo;

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export const Blog = () => {
  const { content } = useContent();
  const bp = content.blogPage || { eyebrow: "Journal", heading: "Notes on the journey", subtitle: "" };
  const blogEnabled = content.blogPage?.enabled !== false;
  const [posts, setPosts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    if (!blogEnabled) return;
    listCategories().then(setCategories).catch(() => {});
  }, [blogEnabled]);

  useEffect(() => {
    if (!blogEnabled) return;
    setPosts(null);
    const t = setTimeout(() => {
      listPublishedPosts({ search, category })
        .then(setPosts)
        .catch(() => setPosts([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search, category, blogEnabled]);

  const chips = useMemo(() => ["All", ...categories], [categories]);

  if (!blogEnabled) return <Navigate to="/" replace />;

  const override = getPageSeo(content, "blog");
  const title = composeTitle(override.title || blogSeoDefaults.title, content);
  const description = override.description || blogSeoDefaults.description || getDefaultDescription(content);

  return (
    <div className="bg-cream min-h-screen">
      <SEO path="/blog" title={title} description={description} />
      <Navbar />
      <header className="pt-32 pb-12 bg-white border-b border-cream-2">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <p className="text-xs md:text-sm uppercase tracking-[0.24em] font-semibold text-ocean">{bp.eyebrow}</p>
          <h1 className="mt-3 font-serif font-light text-5xl md:text-6xl tracking-tight text-ink">{bp.heading}</h1>
          <p className="mt-4 text-lg text-ink-soft max-w-2xl">{bp.subtitle}</p>

          <div className="mt-8 flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
              <input
                data-testid="blog-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search posts…"
                className="w-full rounded-full border border-cream-2 bg-cream/50 pl-10 pr-4 py-2.5 text-ink outline-none focus:border-sage"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {chips.map((c) => (
                <button
                  key={c}
                  data-testid={`blog-category-${c}`}
                  onClick={() => setCategory(c)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${category === c ? "bg-sage text-white" : "bg-cream-2 text-ink-soft hover:bg-sand"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 md:px-8 py-14 min-h-[40vh]">
        {posts === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-sage" /></div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-ink-muted" data-testid="blog-empty">No posts found. Try a different search or category.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {posts.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex flex-col rounded-2xl overflow-hidden border border-cream-2 bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_-28px_rgba(122,143,119,0.5)]">
                <div className="aspect-[16/10] overflow-hidden bg-cream-2">
                  {p.cover_image ? (
                    <img src={mediaUrl(p.cover_image)} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-sage/20 to-peach/20" />
                  )}
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs text-ink-muted">
                    <span className="text-sage-dark font-semibold uppercase tracking-wide">{p.category}</span>
                    <span>·</span>
                    <span>{fmtDate(p.published_at)}</span>
                  </div>
                  <h2 className="mt-2 font-serif text-2xl text-ink leading-tight">{p.title}</h2>
                  <p className="mt-2 text-sm text-ink-soft leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sage-dark">Read more <ArrowUpRight className="h-4 w-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
